import {isFilled} from '@prismicio/client'
import type {
  ArticleDocument,
  BlogPostDocument,
  ButtonDocument,
  CustomerStoryDocument,
  DialogDocumentData,
  EventDocument,
  GenericFormDocument,
  GlossaryPageDocument,
  GlossaryTermDocument,
  NavigationDocumentData,
  PageDocument,
  ProductAnnouncementCardDocument,
  ProductDeepDiveDocumentData,
} from 'prismicio-types'
import {memo} from 'react'
import type {FraudGameQuestionDocument} from 'slices/FraudGame/data'
import {Article} from 'src/Article'
import {BlogPost} from 'src/BlogPost'
import {CustomerStory} from 'src/CustomerStory'
import {Event} from 'src/Event'
import {Glossary} from 'src/Glossary'
import {GlossaryTerm} from 'src/GlossaryTerm'
import {Page} from 'src/Page'
import type {ParentUid} from 'src/common/utils/documentHelpers'
import {getBreadcrumbs} from 'src/common/utils/documentHelpers'
import type {EventType} from 'src/common/utils/eventRoutes'

/**
 * PageRouter Component
 *
 * Routes different document types to their specialized components.
 * Uses TypeScript discriminated unions for type-safe routing.
 */
export const PageRouter = memo(function (props: {
  articles?: Array<ArticleDocument>
  customerStories?: Array<CustomerStoryDocument>
  customerStoriesData?: Array<CustomerStoryDocument>
  navigationData: NavigationDocumentData
  page:
    | PageDocument
    | ArticleDocument
    | CustomerStoryDocument
    | BlogPostDocument
    | GlossaryPageDocument
    | GlossaryTermDocument
    | EventDocument
  parentUids?: Array<ParentUid>
  productDeepDiveData?: ProductDeepDiveDocumentData
  formData?: GenericFormDocument
  dialogData?: DialogDocumentData
  productAnnouncementsData?: Array<ProductAnnouncementCardDocument>
  fraudGameQuestions?: FraudGameQuestionDocument[]
  moreCustomerStories?: Array<CustomerStoryDocument>
  buttonsData?: Array<ButtonDocument>
  categories?: Array<{category_name: string; value: string}>
  blogPosts?: Array<BlogPostDocument<string>>
  totalPages?: number
  glossaryTerms?: Array<GlossaryTermDocument>
  relatedTerms?: Array<GlossaryTermDocument>
  // Event-specific props
  relatedEvents?: Array<EventDocument>
  eventsByType?: Record<string, EventDocument<string>[]>
  eventTypes?: EventType[]
  customBackground?: string
}) {
  // Type-safe routing using discriminated unions
  switch (props.page.type) {
    case 'article':
      return (
        <Article
          navigationData={props.navigationData}
          page={props.page}
          parentUids={props.parentUids}
          breadcrumbs={getBreadcrumbs(props.page)}
          buttonsData={props.buttonsData}
          articles={props.articles}
        />
      )

    case 'customer_story':
      return (
        <CustomerStory
          navigationData={props.navigationData}
          page={props.page}
          parentUids={props.parentUids}
          breadcrumbs={getBreadcrumbs(props.page)}
          moreCustomerStories={props.moreCustomerStories}
          buttonsData={props.buttonsData}
          articles={props.articles}
        />
      )

    case 'event':
      return (
        <Event
          navigationData={props.navigationData}
          page={props.page}
          parentUids={props.parentUids}
          buttonsData={props.buttonsData}
          relatedEvents={props.relatedEvents}
        />
      )

    case 'blog_post':
      return (
        <BlogPost
          navigationData={props.navigationData}
          page={props.page}
          parentUids={props.parentUids}
          buttonsData={props.buttonsData}
          categories={props.categories}
          blogPosts={props.blogPosts}
          totalPages={typeof props.totalPages === 'number' ? props.totalPages : undefined}
        />
      )

    case 'page':
      return (
        <Page
          navigationData={props.navigationData}
          page={props.page}
          parentUids={props.parentUids}
          productDeepDiveData={props.productDeepDiveData}
          formData={props.formData}
          dialogData={props.dialogData}
          productAnnouncementsData={props.productAnnouncementsData}
          fraudGameQuestions={props.fraudGameQuestions}
          buttonsData={props.buttonsData}
          articles={props.articles}
          customerStories={props.customerStories}
          customerStoriesData={props.customerStoriesData}
          blogPosts={props.blogPosts}
          categories={props.categories}
          totalPages={props.totalPages}
          eventsByType={props.eventsByType}
          eventTypes={props.eventTypes}
          customBackground={props.customBackground}
        />
      )

    case 'glossary_page':
      return (
        <Glossary navigationData={props.navigationData} page={props.page} glossaryTerms={props.glossaryTerms || []} />
      )

    case 'glossary_term':
      if (!isFilled.contentRelationship(props.page.data.parent_page)) {
        throw new Error('Glossary term requires parentPage')
      }

      return (
        <GlossaryTerm
          navigationData={props.navigationData}
          term={props.page}
          parentPage={props.page.data.parent_page}
          relatedTerms={props.relatedTerms || []}
          buttonsData={props.buttonsData}
        />
      )

    default:
      throw new Error(`Unhandled page type: ${(props.page as {type: string}).type}`)
  }
})
